import React, { useState } from 'react'
import styled from '@emotion/styled'
import { v4 as uuidv4 } from 'uuid'
import { AccountHeader } from '@mobi/component-library/Account'
import { Button, Password } from '@mobi/component-library/Common/V2'
import { colors, font } from '@mobi/component-library/Theme/Common'
import { Content, Footer, SignUpPageStyled } from './SignUpPage.styled'
import { useDispatch, useSelector } from 'react-redux'
import { getSignUpState, setIsNewUserAutoLoggedIn, setNewUserBetAccount } from '../Store'
import { useMutation } from '@tanstack/react-query'
import { post, queryKeys, SignUpUrl } from '@mobi/account/Utils'
import type { AttemptLoginReturn, LogonRequestData } from '@mobi/api-types'
import { SignupError } from './SignupError'
import { useNavigate } from 'react-router'
import { routes } from '../Utils'
import {
  trackSignUpCreatePassword,
  trackSignUpLoginFailed,
  trackSignUpComplete,
  trackSignUpError,
} from '../Utils/ga'
import { ACCOUNT_CREATED_OPTIMOVE_EVENT } from '../Utils'
import { Contract } from '@mobi/web-native-comms'
import { PasswordCriteria, validatePassword } from './PasswordValidate'
import { PasswordCriteriaList } from './PasswordCriteriaList'
import { unwrapErrorMessage } from '@mobi/utils/errors'

type PropPassword = {
  onCompleted?: (password: string) => void
  attemptLogin: (data: LogonRequestData) => Promise<AttemptLoginReturn>
  trackOptimoveEvent: (event: Contract['OPTIMOVE_TRACK']) => void
}

type TypeCreateBetAccountResponse = {
  betAccountNumber: number
}

type TypeCreateBetAccountRequest = {
  applicationReferenceNumber: number
  password: string
  enquiryId: string
}

export const PasswordConfirm = ({
  onCompleted,
  attemptLogin,
  trackOptimoveEvent,
}: PropPassword) => {
  const [password, setPassword] = useState('')
  const [passwordUnmetCriteria, setPasswordUnmetCriteria] = useState<PasswordCriteria[]>([
    PasswordCriteria.containsNumberOrSpecialCharacter,
    PasswordCriteria.containsUpperAndLowerLetters,
    PasswordCriteria.sufficientLength,
  ])
  const [confirmationPassword, setConfirmationPassword] = useState('')
  const [showErrorPopup, setShowErrorPopup] = useState(false)
  const signUpState = useSelector(getSignUpState)
  const dispatch = useDispatch()
  const navigate = useNavigate()

  const isPasswordValid = passwordUnmetCriteria.length === 0
  const [confirmationAttempted, setConfirmationAttempted] = useState(false)
  if (
    !confirmationAttempted &&
    !!confirmationPassword &&
    confirmationPassword.length >= password.length
  ) {
    setConfirmationAttempted(true)
  }

  const canContinue = isPasswordValid && password === confirmationPassword
  const confirmationPasswordError =
    confirmationAttempted && password !== confirmationPassword ? 'The passwords do not match.' : ''

  const createWageringAccount = useMutation<
    TypeCreateBetAccountResponse,
    Error,
    TypeCreateBetAccountRequest
  >({
    mutationFn: ({
      applicationReferenceNumber,
      password,
      enquiryId,
    }: TypeCreateBetAccountRequest) => {
      return post(SignUpUrl.createBetAccount, {
        applicationReferenceNumber,
        password,
        enquiryId,
      })
    },
    mutationKey: [queryKeys.signupCreateWageringBetAccount],
    onSuccess: async (data, { password }) => {
      trackSignUpCreatePassword()
      trackSignUpComplete()
      trackOptimoveEvent(ACCOUNT_CREATED_OPTIMOVE_EVENT)

      const { betAccountNumber } = data
      const accountIdentifier = betAccountNumber.toString()
      const newDeviceSecret = uuidv4()

      const result = await attemptLogin({
        accountIdentifier,
        password,
        keepLoggedIn: false,
        rememberAccount: false,
        deviceSecret: newDeviceSecret,
      })

      if (result.status === 'FAILED' || result.errorMessage) {
        dispatch(setIsNewUserAutoLoggedIn(false))
        navigate(routes.signUpUnsuccessful)
        trackSignUpLoginFailed(betAccountNumber)
        return
      }

      if (result.status === 'SUCCESSFUL') {
        onCompleted?.(password)

        dispatch(setIsNewUserAutoLoggedIn(true))
        dispatch(setNewUserBetAccount(betAccountNumber))

        navigate(routes.phoneNumberVerification)
      } else {
        throw new Error('Unable to login with created bet account')
      }
    },
    onError: error => {
      trackSignUpError(location.pathname, unwrapErrorMessage(error))
      setShowErrorPopup(true)
    },
  })

  return (
    <>
      <SignUpPageStyled>
        <AccountHeader
          alignItems='start'
          title='Create your password'
          description='Create a password to keep your account secured.'
          style={{ marginBottom: '0rem' }}
        />
        <Content>
          <PasswordInputStyled>
            <Password
              name='password'
              label='Password'
              value={password}
              onInput={e => {
                const newValue = e.currentTarget.value
                setPassword(newValue)

                const { unmetCriteria } = validatePassword(newValue)
                setPasswordUnmetCriteria(unmetCriteria)
              }}
              isDisabled={createWageringAccount.isPending}
              isInvalid={passwordUnmetCriteria.includes(PasswordCriteria.containsComma)}
              helpText={
                passwordUnmetCriteria.includes(PasswordCriteria.containsComma)
                  ? 'Commas are not valid. Please use a different character.'
                  : ''
              }
            />

            <PasswordCriteriaList unmetCriteria={passwordUnmetCriteria} />

            <Password
              name='confirm-password'
              label='Confirm Password'
              isInvalid={!!confirmationPasswordError}
              helpText={confirmationPasswordError}
              value={confirmationPassword}
              onInput={e => setConfirmationPassword(e.currentTarget.value)}
              isDisabled={createWageringAccount.isPending}
            />
          </PasswordInputStyled>
        </Content>
        <Footer>
          <Button
            aria-label='Continue button'
            color='primary'
            isFullWidth
            size='md'
            disabled={!canContinue}
            isLoading={createWageringAccount.isPending}
            onClick={() => {
              createWageringAccount.mutate({
                applicationReferenceNumber:
                  signUpState?.signupVerification?.applicationReferenceNumber ?? 0,
                password: password,
                enquiryId: signUpState?.signupVerification?.enquiryId ?? '',
              })
            }}
          >
            Continue
          </Button>
          <HelperText>Complete all fields before continuing</HelperText>
        </Footer>
      </SignUpPageStyled>
      <SignupError
        isOpen={showErrorPopup}
        applicationId={signUpState?.signupVerification?.applicationReferenceNumber?.toString()}
        onClose={() => {
          createWageringAccount.reset()
          setShowErrorPopup(false)
        }}
      />
    </>
  )
}

export const HelperText = styled.div({
  fontFamily: font.family.primary,
  color: colors.neutral[600],
  ...font.size.md,
})

export const PasswordInputStyled = styled.div({
  display: 'flex',
  flexDirection: 'column',
  gap: '1rem',
})
